import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { LangProvider } from "./lib/i18n";
import { RegionProvider, useRegion } from "./context/RegionContext";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { AppDataProvider } from "./context/AppDataContext";
import SwipeShell from "./components/SwipeShell";
import { LoginPromptDialog, BirthDateDialog } from "./components/Dialogs";
import AdultGateDialog from "./components/AdultGate";
import { Spinner } from "./components/common";
import DetailPage from "./pages/DetailPage";
import PublishPage from "./pages/PublishPage";
import MyListingsPage from "./pages/MyListingsPage";
import { FavoritesPage, MyDataPage, SettingsPage } from "./pages/AccountPages";
import MessagesPage from "./pages/MessagesPage";
import ChatPage from "./pages/ChatPage";
import UserProfilePage from "./pages/UserProfilePage";
import { ForgotPasswordPage, ResetPasswordPage } from "./pages/ResetPages";
import { UnreadProvider } from "./context/UnreadContext";
import ErrorBoundary from "./components/ErrorBoundary";
import { AuthPage, AuthCallback } from "./pages/AuthPages";
import LegalPage from "./pages/LegalPage";
import { CookieNotice } from "./components/LegalLinks";

function RequireAuth({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) return <div className="flex min-h-[100dvh] items-center justify-center"><Spinner /></div>;
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  return children;
}

function useDocumentMeta() {
  const { region } = useRegion();
  useEffect(() => {
    document.title = region.brand_title;
  }, [region]);
}

function AppRouter() {
  const location = useLocation();
  useDocumentMeta();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  const guard = (el) => <RequireAuth>{el}</RequireAuth>;
  return (
    <>
      <Routes>
        <Route path="/" element={<SwipeShell />} />
        <Route path="/mercat" element={<SwipeShell />} />
        <Route path="/perfil" element={<SwipeShell />} />
        <Route path="/anuncio/:id" element={<DetailPage />} />
        <Route path="/publicar" element={guard(<PublishPage />)} />
        <Route path="/editar/:id" element={guard(<PublishPage />)} />
        <Route path="/mis-anuncios" element={guard(<MyListingsPage />)} />
        <Route path="/favoritos" element={guard(<FavoritesPage />)} />
        <Route path="/mis-datos" element={guard(<MyDataPage />)} />
        <Route path="/mensajes" element={guard(<MessagesPage />)} />
        <Route path="/mensajes/:id" element={guard(
          <ErrorBoundary title="El chat ha tenido un problema" backHref="/mensajes"><ChatPage /></ErrorBoundary>
        )} />
        <Route path="/usuario/:id" element={<UserProfilePage />} />
        <Route path="/recuperar" element={<ForgotPasswordPage />} />
        <Route path="/restablecer" element={<ResetPasswordPage />} />
        <Route path="/ajustes" element={<SettingsPage />} />
        <Route path="/login" element={<AuthPage mode="login" />} />
        <Route path="/registro" element={<AuthPage mode="register" />} />
        <Route path="/legal/:slug" element={<LegalPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <LoginPromptDialog />
      <BirthDateDialog />
      <AdultGateDialog />
    </>
  );
}

function RegionApp() {
  const { basename } = useRegion();
  return (
    <BrowserRouter basename={basename}>
      <LangProvider>
        <AuthProvider>
          <AppDataProvider>
            <UnreadProvider>
              <ErrorBoundary><AppRouter /></ErrorBoundary>
              <CookieNotice />
              <Toaster position="top-center" richColors closeButton toastOptions={{ className: "rounded-2xl" }} />
            </UnreadProvider>
          </AppDataProvider>
        </AuthProvider>
      </LangProvider>
    </BrowserRouter>
  );
}

export default function App() {
  return (
    <RegionProvider>
      <RegionApp />
    </RegionProvider>
  );
}
